import sum from 'lodash/sum';
import keys from 'lodash/keys';
import omit from 'lodash/omit';
import first from 'lodash/first';
import filter from 'lodash/filter';
import concat from 'lodash/concat';
import without from 'lodash/without';
import isInteger from 'lodash/isInteger';
import pickBy from 'lodash/pickBy';

import { round4 } from 'utilities/math';

import {
  RAW,
  FREQUENCY,
  CONTINGENCY,
  RELATIVE_FREQUENCY
} from 'scenes/Dataset/components/constants';

export const extractFreqColumnsAndCategoryCol = (dataSet, colsToOmit = []) => {
  // Step 1: Get first Row as Object
  const firstRowAsObj = first(dataSet);
  // Step 2: Get all the headers
  const columnNames = keys(firstRowAsObj);
  // Step 3: Pick Headers that has String column (Category Column)
  // We expect dataset to have only one String Column
  const stringColumnNames = keys(pickBy(firstRowAsObj, value => {
    return isNaN(value) && typeof value !== 'object';
  }));

  let categoryColumnName = null;
  let frequencyColumnNames = null;
  let orderedColumnNames = null;

  /*
  * Case 1:  (One String Column)
  * */
  // Step 4: Pick Headers that has String column (Category Column)
  if (stringColumnNames && stringColumnNames.length) {
    [categoryColumnName] = stringColumnNames;
    // Step 5: Extract remaining columns (Frequency Columns)
    frequencyColumnNames = without(columnNames, categoryColumnName, ...colsToOmit);
    // Step 6: Order the ColumnNames: Category Column followed by Frequency Columns
    orderedColumnNames = [categoryColumnName, ...frequencyColumnNames];

    return {
      categoryColumn: categoryColumnName,
      frequencyColumns: frequencyColumnNames,
      orderedColumns: orderedColumnNames
    };
  }

  /*
  * Case 2: (No string Column but we have Header name in Number format. eg: 2020)
  * */
  // Step 4.1: If there are No String columns
  // Case 1: Among numeric columns, we might have numeric representation for headers
  // example: Columns Names [2019, Header1, header2]
  const columnNamesAsNumber = keys(pickBy(firstRowAsObj, (value, key) => {
    return !isNaN(key);
  }));
  if (columnNamesAsNumber && columnNamesAsNumber.length) {
    // JS Object.Keys will return sorted numbers followed by String keys based on its insertion order
    // Since Numeric Header names are used for Frequency columns,
    // we are pushingNumeric Header names to back
    const columnNamesAsStrings = without(columnNames, ...columnNamesAsNumber);
    const tempOrderedColumnNames = [...columnNamesAsStrings, ...columnNamesAsNumber];

    categoryColumnName = first(tempOrderedColumnNames);
    frequencyColumnNames = without(tempOrderedColumnNames, categoryColumnName, ...colsToOmit);
    orderedColumnNames = [categoryColumnName, ...frequencyColumnNames];
  } else {

    /*
     * Case 3: (No string Column)
     * */
    categoryColumnName = first(columnNames);
    frequencyColumnNames = without(columnNames, categoryColumnName, ...colsToOmit);
    orderedColumnNames = [categoryColumnName, ...frequencyColumnNames];
  }

  return {
    categoryColumn: categoryColumnName,
    frequencyColumns: frequencyColumnNames,
    orderedColumns: orderedColumnNames
  };
};

export const getCalculateFrequencyParameters = (dataSet, nameForFrequencyColumns = 'Category') => {
  const { orderedColumns: datasetHeaders, categoryColumn: nameOfLevels } = extractFreqColumnsAndCategoryCol(dataSet);

  const frequencyParameters = datasetHeaders.length === 2 ? [nameOfLevels] : [nameOfLevels, nameForFrequencyColumns];
  return frequencyParameters.reduce((previous, current) => {
    previous[current] = {
      name: current,
      targetType: 'categorical'
    };
    return previous;
  }, {});
};

export const getCalculateFrequencyDataSet = (dataSet, nameForFrequencyColumns = 'Category') => {
  const { orderedColumns: datasetHeaders, categoryColumn: nameOfLevels, frequencyColumns: keysFrequency } = extractFreqColumnsAndCategoryCol(dataSet);

  const total = round4(dataSet.reduce((previous, current) => {
    const filtered = filter(current, dataSetCell => {
      return dataSetCell !== current[nameOfLevels];
    });
    const totalRow = sum(filtered);
    return previous + totalRow;
  }, 0));

  const getProportion = (count, totalAll) => round4(count / totalAll);

  const levelsOfLevels = dataSet.map(datasetRow => {
    const totalLevel = round4(sum(filter(datasetRow, dataSetCell => dataSetCell !== datasetRow[nameOfLevels])));
    const levelsObject = omit(datasetRow, nameOfLevels);
    const rebuildLevels = keys(levelsObject).map(key => ({
      name: key,
      count: levelsObject[key]
    }));
    return {
      name: datasetRow[nameOfLevels].toString(),
      count: totalLevel,
      levels: rebuildLevels,
      proportion: getProportion(totalLevel, total)
    };
  });

  const levelsOfFrequency = keysFrequency.map(key => {
    const totalFrequency = round4(sum(dataSet.map(datasetRow => datasetRow[key])));
    const frequencyLevels = dataSet.map(datasetRow => {
      return {
        name: datasetRow[nameOfLevels],
        count: datasetRow[key]
      };
    });
    return {
      name: key,
      count: totalFrequency,
      levels: frequencyLevels,
      proportion: getProportion(totalFrequency, total)
    };
  });

  const namesOfDataRow = datasetHeaders.length === 2 ? [nameOfLevels] : [nameOfLevels, nameForFrequencyColumns];

  return namesOfDataRow.map((name, i) => {
    const getLevels = () => i ? levelsOfFrequency : levelsOfLevels;

    return {
      [name]: {
        name,
        count: total,
        levels: getLevels()
      }
    };
  });
};

export const getTypeOfFrequencyDataSet = dataSet => {
  // Object keys ordering in JS
  // 1. Numbers are ordered first, and they are ordered within themselves from smallest to largest
  // 2. Strings come second, and they are ordered within themselves by insertion order
  // Hence this approach is used to filter Numeric Values from Numeric Column

  const { frequencyColumns } = extractFreqColumnsAndCategoryCol(dataSet);

  const valuesFromAllNumericColumns = dataSet.reduce((items, dataSetRow) => {
    const frequency = frequencyColumns.map(column => dataSetRow[column]);

    return concat(items, frequency);
  }, []);

  const isFrequency = valuesFromAllNumericColumns.every(item => item >= 0 && isInteger(item));
  let typeOfFrequency = '';
  if (isFrequency) {
    typeOfFrequency = FREQUENCY;
    if (frequencyColumns.length > 1) {
      typeOfFrequency = CONTINGENCY;
    }
  }

  const sumOfAllNumericValues = round4(sum(valuesFromAllNumericColumns));
  if (sumOfAllNumericValues === 1) {
    const isRelativeFrequency = valuesFromAllNumericColumns.every(item => item >= 0 && item < 1);
    typeOfFrequency = isRelativeFrequency ? RELATIVE_FREQUENCY : typeOfFrequency;
  }

  return typeOfFrequency;
};

export const identifyTypeOfFrequencyDataSet = (dataSet, tableType) => {
  const typeDatasetFrequencyOrRelative = getTypeOfFrequencyDataSet(dataSet);
  let type = RAW;
  if (typeDatasetFrequencyOrRelative.length > 0) {
    const { frequencyColumns } = extractFreqColumnsAndCategoryCol(dataSet);
    if (frequencyColumns.length > 1) {
      type = CONTINGENCY;
    } else {
      type = FREQUENCY;
    }
  }

  if (tableType !== type.toLowerCase()) {
    return {
      status: 'error',
      type
    };
  }

  return {
    status: 'success',
    type
  };
};


export const getCalculateFrequencyDatasetSummary = frequencyDataset => {
  const categoricalSummary = frequencyDataset.reduce((previous, current) => {
    const key = first(keys(current));
    previous[key] = current[key];
    return previous;
  }, {});

  return ({
    categoricalSummary,
    numericalSummary: {}
  });
};
